import React, { useEffect, useState, useRef } from "react";
import {
  IconComment,
  IconLike,
  IconViews,
  IconShare,
  IconRepost,
  IconMore,
  IconSupport,
} from "../components/FeedIcons";
import { formatPostTime } from "../utils/formatPostTime";
import MediaGrid from "../components/MediaGrid";
import useZoomModal from "../hooks/useZoomModal";

/* 🔗 LINKIFY — makes links in text blue + clickable.
   Matches https://…, http://…, www.… and bare domains like coins.metisel.com */
const LINK_REGEX = /(?:https?:\/\/|www\.)[^\s<]+|(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+(?:com|net|org|io|co|app|dev|xyz|info|me|ai|gh|uk|us|ng|edu|gov|tv|site|online|store|tech|biz|ly|to|cc)\b(?::\d{2,5})?(?:[/?#][^\s<]*)?/gi;

const splitLinks = (text) => {
  const out = [];
  let last = 0;
  let m;
  LINK_REGEX.lastIndex = 0;
  while ((m = LINK_REGEX.exec(text))) {
    const start = m.index;
    const isBare = !/^(?:https?:\/\/|www\.)/i.test(m[0]);
    // bare domain glued to an email / word (user@site.com) → not a link
    if (isBare && start > 0 && /[@\w.-]/.test(text[start - 1])) continue;
    // keep trailing punctuation out of the link
    const url = m[0].replace(/[.,;:!?)\]}'"]+$/, "");
    if (!url) continue;
    if (start > last) out.push({ text: text.slice(last, start) });
    out.push({ url });
    last = start + url.length;
    LINK_REGEX.lastIndex = last;
  }
  if (last < text.length) out.push({ text: text.slice(last) });
  return out;
};

const LinkifiedText = ({ text }) => {
  if (!text) return null;

  return splitLinks(String(text)).map((part, i) => {
    if (!part.url) return <React.Fragment key={i}>{part.text}</React.Fragment>;

    const href = /^https?:\/\//i.test(part.url) ? part.url : `https://${part.url}`;
    return (
      <a
        key={i}
        href={href}
        target="_blank"
        rel="noopener noreferrer nofollow"
        onClick={(e) => e.stopPropagation()} // don't trigger the parent's click
        className="text-blue-500 hover:underline break-all"
      >
        {part.url}
      </a>
    );
  });
};

// "Show more" cut must not land in the middle of a link
const sliceKeepingLinks = (text, limit) => {
  let end = limit;
  let pos = 0;
  for (const p of splitLinks(text)) {
    const len = (p.url ?? p.text).length;
    if (p.url && pos < end && pos + len > end) {
      end = pos + len;
      break;
    }
    pos += len;
  }
  return text.slice(0, end);
};

export default function CommentCard({
  item,
  isDeleted,
  isEdited,
  myChatId,
  resolveUserAvatar,
  highlightId,
  handleQuoteClick,
  closeAllReplies,
  setReplyingCommentId,
  setQuoteTarget,
  onViewComment,
  onLikeComment,
  onOpenRepostMenu,
  autoExpandIds,
  replyingCommentId,
  setMenu,
  commentReplyText,
  setCommentReplyText,
  onSubmitReply,
  editingCommentId,
  editingCommentText,
  setEditingCommentText,
  onSaveEdit,
  onCancelEdit,
  commentReplyMediaFiles,
  commentReplyMediaPreview,
  setCommentReplyMediaFiles,
  setCommentReplyMediaPreview,
  onSelectReplyMedia,
  onSupportComment,   // ← ({ comment }) => void  — open "send support" modal (other users' comments)
  onViewCommentEarnings, // ← ({ comment }) => void — open "earnings" view (own comments)
    onOpenUser,
}) {
  const avatar = resolveUserAvatar(item.author?.chatId);
  const isHighlighted = highlightId === `comment-card-${item.commentId}`;
  const replyTextareaRef = useRef(null);

  const LIMIT = 100;
  const [textExpanded, setTextExpanded] = useState(false);
  const [expanded, setExpanded] = useState(autoExpandIds?.has(item.commentId));

  const [zoomOpen, setZoomOpen] = useState(false);
  const [zoomSrc, setZoomSrc] = useState(null);
  const [zoomType, setZoomType] = useState("image");

  const zoomModal = useZoomModal({ zoomOpen, setZoomOpen, zoomSrc, zoomType });

  const handleZoomMedia = (m) => {
    setZoomSrc(m.url);
    setZoomType(m.type);
    setZoomOpen(true);
  };

  useEffect(() => {
    if (autoExpandIds?.has(item.commentId)) setExpanded(true);
  }, [autoExpandIds, item.commentId]);

  useEffect(() => {
    if (replyingCommentId === item.commentId) {
      setTimeout(() => replyTextareaRef.current?.focus(), 0);
    }
  }, [replyingCommentId, item.commentId]);

  useEffect(() => {
    if (item?.commentId) onViewComment?.({ commentId: item.commentId });
  }, [item?.commentId]);

  const hasChildren = item.children?.length > 0;
  const childrenCount = item.children?.length || 0;
  const visibleChildren = expanded ? item.children : [];

  const fmt = (n) =>
    !n ? null : n >= 1000 ? `${(n / 1000).toFixed(1)}k` : String(n);

  const isCommentAuthor = String(item.author?.chatId) === String(myChatId);

  const handleSupportComment = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (isCommentAuthor) {
      onViewCommentEarnings?.({ comment: item });
    } else {
      onSupportComment?.({ comment: item });
    }
  };

  const showCommentSupport =
    (isCommentAuthor && onViewCommentEarnings) ||
    (!isCommentAuthor && onSupportComment && myChatId);

  return (
    <>
      <div
        id={`comment-card-${item.commentId}`}
        className={[
          "px-3 sm:px-4 py-3 border-b border-neutral-200 dark:border-neutral-700",
          "scroll-mt-28 transition-all duration-500",
          isHighlighted
            ? "ring-2 ring-blue-500 ring-inset bg-blue-50 dark:bg-blue-950/20"
            : "",
        ].join(" ")}
      >
        <div className="flex gap-3">
          {/* AVATAR */}
         <div
  className="shrink-0 cursor-pointer"
  onClick={(e) => {
    e.stopPropagation();
    onOpenUser?.(item.author?.chatId);
  }}
>
  {avatar ? (
    <img
      src={avatar}
      className="w-9 h-9 sm:w-10 sm:h-10 rounded-full object-cover"
      alt=""
    />
  ) : (
    <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-full bg-neutral-300 dark:bg-neutral-700 flex items-center justify-center text-sm">
      👤
    </div>
  )}
</div>

          {/* CONTENT */}
          <div className="flex-1 min-w-0">

            {/* REPOST / QUOTE LABEL */}
            {item.isRepost && !item.isQuote && (
              <div className="text-xs text-neutral-500 dark:text-neutral-400 mb-1 flex items-center gap-1">
                <span>🔁</span>
                <span>{item.author?.displayName || item.author?.username} reposted</span>
              </div>
            )}
            {item.isQuote && (
              <div className="text-xs text-neutral-500 dark:text-neutral-400 mb-1 flex items-center gap-1">
                <span>✏️</span>
                <span>{item.author?.displayName || item.author?.username} quoted</span>
              </div>
            )}

            {/* HEADER ROW */}
            <div className="flex items-start justify-between gap-2">
           <div className="flex items-center gap-1.5 flex-wrap text-sm min-w-0">
  <span className="font-semibold text-neutral-800 dark:text-neutral-200 truncate max-w-[110px] sm:max-w-none">
    <span
      onClick={(e) => {
        e.stopPropagation();
        onOpenUser?.(item.author?.chatId);
      }}
      className="cursor-pointer hover:underline"
    >
      {item.author?.displayName || item.author?.username}
    </span>
  </span>

  <span className="text-neutral-400 inline truncate max-w-[90px]">
    <span
      onClick={(e) => {
        e.stopPropagation();
        onOpenUser?.(item.author?.chatId);
      }}
      className="cursor-pointer hover:underline"
    >
      @{item.author?.username}
    </span>
  </span>

  <span className="text-neutral-400 inline">·</span>

  <span className="text-neutral-400 text-xs whitespace-nowrap">
    {formatPostTime(item.createdAt)}
  </span>

  {isEdited && !isDeleted && (
    <span className="text-xs text-neutral-400">(edited)</span>
  )}
</div>
              {/* 3-DOT */}
              {!isDeleted && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    const isAuthor = String(item.author?.chatId) === String(myChatId);
                    setMenu({
                      open: true,
                      post: item,
                      type: isAuthor ? "comment-actions" : "post-report",
                    });
                  }}
                                   className="shrink-0 flex items-center justify-center rounded-full text-neutral-400 hover:text-neutral-600 dark:hover:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-800 active:scale-95 transition-all touch-manipulation -mr-1 -my-1"
                  style={{ minWidth: 24, minHeight: 24 }}
                  aria-label="More options"
                >
                  <IconMore />
                </button>
              )}
            </div>

{/* REPOST ORIGINAL */}
{item.isRepost && item.originalComment && (
  <div className="mt-2 rounded-2xl border border-neutral-200 dark:border-neutral-700 bg-neutral-50 dark:bg-neutral-800/60 p-3">
    <div className="text-xs text-neutral-500 dark:text-neutral-400 mb-1">Original comment</div>
    <div className="font-semibold text-sm text-neutral-800 dark:text-neutral-200">
      {item.originalComment.author?.displayName || item.originalComment.author?.username}
    </div>
    {item.originalComment.text && (
      <div className="text-sm whitespace-pre-wrap break-words text-neutral-700 dark:text-neutral-300">
        <LinkifiedText text={item.originalComment.text} />
      </div>
    )}
    {/* ✅ NEW — render the original comment's media */}
    {item.originalComment.media?.length > 0 && (
      <div className="mt-2">
        <MediaGrid media={item.originalComment.media} onClick={handleZoomMedia} />
      </div>
    )}
  </div>
)}
            {/* BODY — click to expand replies */}
            {!item.isRepost && (
              <div
                className={`mt-0 text-[15px] whitespace-pre-wrap break-words leading-relaxed text-neutral-800 dark:text-neutral-200 ${
                  hasChildren ? "cursor-pointer" : ""
                }`}
                onClick={() => { if (hasChildren) setExpanded((prev) => !prev); }}
              >
                {isDeleted ? (
                  <p className="text-sm text-neutral-400 italic">
                    This comment was deleted.
                  </p>
                ) : editingCommentId === item.commentId ? (
                  <div className="mt-1">
                    <textarea
                      className="w-full border border-neutral-300 dark:border-neutral-600 rounded-xl p-2.5 text-sm bg-white dark:bg-neutral-800 dark:text-neutral-100 resize-none focus:outline-none focus:ring-2 focus:ring-blue-400"
                      value={editingCommentText}
                      onChange={(e) => setEditingCommentText(e.target.value)}
                      rows={3}
                      autoFocus
                    />
                    <div className="flex gap-3 mt-2">
                      <button
                        onClick={() => onSaveEdit(item.commentId)}
                        className="text-blue-500 font-semibold text-sm touch-manipulation"
                        style={{ minHeight: 36 }}
                      >
                        Save
                      </button>
                      <button
                        onClick={onCancelEdit}
                        className="text-neutral-500 text-sm touch-manipulation"
                        style={{ minHeight: 36 }}
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  item.text && (() => {
                    const isLong = item.text.length > LIMIT;
                    const displayText =
                      textExpanded || !isLong
                        ? item.text
                        : sliceKeepingLinks(item.text, LIMIT) + "...";
                    return (
                      <>
                        <p className="whitespace-pre-wrap break-words"><LinkifiedText text={displayText} /></p>
                        {isLong && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setTextExpanded((prev) => !prev);
                            }}
                            className="mt-1 text-blue-500 font-medium text-sm touch-manipulation"
                            style={{ minHeight: 36 }}
                          >
                            {textExpanded ? "Show less" : "Show more"}
                          </button>
                        )}
                      </>
                    );
                  })()
                )}
              </div>
            )}

            {/* MEDIA */}
            {!item.isRepost && item.media?.length > 0 && (
              <div className="mt-2">
                <MediaGrid media={item.media} onClick={handleZoomMedia} />
              </div>
            )}

            {/* ── ACTION BAR (hidden when deleted) ── */}
            {!isDeleted && (
                          <div
                             className="-mt-2 flex items-center text-neutral-500 dark:text-neutral-400"
                onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}
              >
                {/* LEFT CLUSTER */}
                <div className="flex items-center justify-between flex-1 pr-2">

                  {/* REPLY */}
               <button
                    onClick={(e) => {
                      e.stopPropagation();
                      if (typeof closeAllReplies === "function") closeAllReplies();
                      if (typeof setReplyingCommentId === "function") setReplyingCommentId(item.commentId);
                    }}
                    className="flex items-center gap-1.5 group touch-manipulation"
                    style={{ minHeight: 44, minWidth: 32 }}
                    aria-label="Reply"
                  >
                    <span className="group-hover:text-blue-500 group-active:scale-90 transition-all duration-100">
                      <IconComment />
                    </span>
                    {fmt(item.commentsCount) && (
                      <span className="text-xs group-hover:text-blue-500 tabular-nums leading-none">
                        {fmt(item.commentsCount)}
                      </span>
                    )}
                  </button>

                  {/* REPOST */}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onOpenRepostMenu?.(item);
                    }}
                    className="flex items-center gap-1.5 group touch-manipulation"
                    style={{ minHeight: 44, minWidth: 32 }}
                    aria-label="Repost"
                  >
                    <span className="group-hover:text-green-500 group-active:scale-90 transition-all duration-100">
                      <IconRepost />
                    </span>
                    {fmt(item.repostsCount) && (
                      <span className="text-xs group-hover:text-green-500 tabular-nums leading-none">
                        {fmt(item.repostsCount)}
                      </span>
                    )}
                  </button>

                  {/* LIKE */}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onLikeComment?.({ commentId: item.commentId, chatId: myChatId });
                    }}
                    className="flex items-center gap-1.5 group touch-manipulation"
                    style={{ minHeight: 44, minWidth: 32 }}
                    aria-label="Like"
                  >
                    <span
                      className={`group-active:scale-90 transition-all duration-100 ${
                        item.likedByMe ? "text-red-500" : "group-hover:text-red-500"
                      }`}
                    >
                      <IconLike filled={item.likedByMe} />
                    </span>
                    {fmt(item.likesCount) && (
                      <span
                        className={`text-xs tabular-nums leading-none ${
                          item.likedByMe ? "text-red-500" : "group-hover:text-red-500"
                        }`}
                      >
                        {fmt(item.likesCount)}
                      </span>
                    )}
                  </button>

                  {/* VIEWS */}
                  <div
                    className="flex items-center gap-1.5 select-none"
                    style={{ minHeight: 44 }}
                    aria-label={`${item.viewsCount || 0} views`}
                  >
                    <IconViews />
                    {fmt(item.viewsCount) && (
                      <span className="text-xs tabular-nums leading-none">
                        {fmt(item.viewsCount)}
                      </span>
                    )}
                  </div>

                  {/* SUPPORT / TIP — send for others' comments, earnings view for own comments */}
                  {showCommentSupport && (
                    <button
                      type="button"
                      onClick={handleSupportComment}
                      className="flex items-center gap-1.5 group touch-manipulation"
                      style={{ minHeight: 44, minWidth: 32 }}
                      aria-label={isCommentAuthor ? "View earnings" : "Support with USWD"}
                      title={isCommentAuthor ? "View earnings" : "Support with USWD"}
                    >
                      <span className="group-hover:text-yellow-500 group-active:scale-90 transition-all duration-100">
                        <IconSupport />
                      </span>
                      {fmt(item.supportsCount) && (
                        <span className="text-xs tabular-nums leading-none group-hover:text-yellow-500">
                          {fmt(item.supportsCount)}
                        </span>
                      )}
                    </button>
                  )}
                </div>

                {/* SHARE */}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setMenu?.({ open: true, post: item, type: "share" });
                  }}
                  className="flex items-center justify-center hover:text-blue-500 active:scale-90 transition-all duration-100 touch-manipulation"
                  style={{ minHeight: 44, minWidth: 32 }}
                  aria-label="Share"
                >
                  <IconShare />
                </button>
              </div>
            )}

            {/* SHOW REPLIES (collapsed) */}
            {hasChildren && !expanded && (
              <div className="mt-1.5">
                <button
                  onClick={(e) => { e.stopPropagation(); setExpanded(true); }}
                  className="text-sm font-medium text-blue-500 hover:underline touch-manipulation"
                  style={{ minHeight: 36 }}
                >
                  Show replies ({childrenCount})
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* INLINE REPLY INPUT */}
      {replyingCommentId === item.commentId && (
        <div className="px-3 sm:px-4 pt-2 pb-3 bg-neutral-50 dark:bg-neutral-800/40 border-b border-neutral-200 dark:border-neutral-700">
          <div className="ml-12">
            <div className="text-xs text-neutral-500 dark:text-neutral-400 mb-2">
              Replying to <span className="text-blue-500">@{item.author?.username}</span>
            </div>
            <textarea
              ref={replyTextareaRef}
              className="w-full border border-neutral-300 dark:border-neutral-600 rounded-xl px-3 py-2 text-sm bg-white dark:bg-neutral-800 dark:text-neutral-100 resize-none focus:outline-none focus:ring-2 focus:ring-blue-400"
              placeholder={`Reply to @${item.author?.username}`}
              value={commentReplyText}
              onChange={(e) => setCommentReplyText(e.target.value)}
              rows={2}
            />
            <div className="mt-2 flex items-center justify-between flex-wrap gap-2">
              <label className="cursor-pointer text-sm text-blue-500 touch-manipulation" style={{ minHeight: 36 }}>
                📎 Media ({commentReplyMediaFiles?.length || 0}/4)
                <input
                  type="file"
                  accept="image/*,video/*"
                  multiple
                  hidden
                  onChange={onSelectReplyMedia}
                />
              </label>
              <div className="flex items-center gap-3">
                <button
                  onClick={() => {
                    if (!commentReplyText.trim() && (commentReplyMediaFiles?.length || 0) === 0) return;
                    onSubmitReply(item.commentId);
                    closeAllReplies();
                  }}
                  className="px-4 py-1.5 rounded-full bg-blue-500 hover:bg-blue-600 active:bg-blue-700 text-white font-semibold text-sm transition-colors touch-manipulation"
                  style={{ minHeight: 36 }}
                >
                  Reply
                </button>
                <button
                  onClick={() => {
                    setCommentReplyMediaFiles([]);
                    setCommentReplyMediaPreview([]);
                    closeAllReplies();
                  }}
                  className="text-neutral-500 text-sm touch-manipulation"
                  style={{ minHeight: 36 }}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* CHILD COMMENTS */}
      {expanded &&
        visibleChildren.map((child) => (
          <CommentCard
            key={child.commentId}
            isDeleted={child.isDeleted}
            isEdited={child.isEdited}
            setMenu={setMenu}
            item={child}
            autoExpandIds={autoExpandIds}
            myChatId={myChatId}
            resolveUserAvatar={resolveUserAvatar}
            highlightId={highlightId}
            handleQuoteClick={handleQuoteClick}
            closeAllReplies={closeAllReplies}
            setReplyingCommentId={setReplyingCommentId}
            setQuoteTarget={setQuoteTarget}
            onLikeComment={onLikeComment}
            onOpenRepostMenu={onOpenRepostMenu}
            onViewComment={onViewComment}
            replyingCommentId={replyingCommentId}
            commentReplyText={commentReplyText}
            setCommentReplyText={setCommentReplyText}
            onSubmitReply={onSubmitReply}
            editingCommentId={editingCommentId}
            editingCommentText={editingCommentText}
            setEditingCommentText={setEditingCommentText}
            onSaveEdit={onSaveEdit}
            onCancelEdit={onCancelEdit}
            commentReplyMediaFiles={commentReplyMediaFiles}
            commentReplyMediaPreview={commentReplyMediaPreview}
            setCommentReplyMediaFiles={setCommentReplyMediaFiles}
            setCommentReplyMediaPreview={setCommentReplyMediaPreview}
            onSelectReplyMedia={onSelectReplyMedia}
            onSupportComment={onSupportComment}
            onViewCommentEarnings={onViewCommentEarnings}
              onOpenUser={onOpenUser}
          />
        ))}

      {/* HIDE REPLIES */}
      {hasChildren && expanded && (
        <div className="px-3 sm:px-4 py-2 border-b border-neutral-200 dark:border-neutral-700">
          <div className="ml-12">
            <button
              onClick={(e) => { e.stopPropagation(); setExpanded(false); }}
              className="text-sm font-medium text-blue-500 hover:underline touch-manipulation"
              style={{ minHeight: 36 }}
            >
              Hide replies
            </button>
          </div>
        </div>
      )}

      {zoomModal}
    </>
  );
}